import type { UI } from '@/v2/types';
import React, { useEffect, useState } from 'react';
import { Box, Container, Flex } from '@qga/roo-ui/components';

import { getTotalTravellers } from '@shared/utils';
import { SkeletonProvider } from '@shared/v2/components/Skeleton';
import { BEST_RATE_ID } from '@shared/v2/constants/packages';
import useAutoSelectedRoomOption from '@shared/v2/hooks/useAutoSelectedRoomOption';
import useFlyingFrom from '@shared/v2/hooks/useFlyingFrom';
import useSearchQuery from '@shared/v2/hooks/useSearchQuery';

import ImportantInformation from '@/components/ImportantInformation';
import FlightOptionsView from '@/components/PackagePage/components/FlightOptionsView';
import QantasPointBanner from '@/components/QantasPointBanner';
import ValueProposition from '@/components/ValueProposition';
import useDestination from '@/hooks/useDestination';
import useRoute from '@/hooks/useRoute';
import { Layout } from '@/v2/components/layout/Layout';
import { PackageOptionContainer } from '@/v2/components/pages/PackageViewV2/components/PackageOptionContainer';
import RoomOptions from '@/v2/components/pages/PackageViewV2/components/RoomOptions';
import Section from '@/v2/components/pages/PackageViewV2/components/Section';
import { PACKAGE_PAGE_NAV_ITEMS } from '@/v2/components/pages/PackageViewV2/constants';
import { useFlightModal } from '@/v2/components/pages/PackageViewV2/contexts/FlightModalContext';
import useAutoTurnOnCampaign from '@/v2/components/pages/PackageViewV2/hooks/useAutoTurnOnCampaign';
import useFlightCampaignAvailability from '@/v2/components/pages/PackageViewV2/hooks/useFlightCampaignAvailability';
import useFlightInclusion from '@/v2/components/pages/PackageViewV2/hooks/useFlightInclusion';
import usePackageOptions from '@/v2/components/pages/PackageViewV2/hooks/usePackageOptions';
import { Divider } from '@/v2/components/ui/Divider';
import InPageNavigation from '@/v2/components/ui/InPageNavigation';
import { usePackagePageV3 } from '@/v2/hooks/featureFlags/packagePageV3/usePackagePageV3';
import { usePointsEarn } from '@/v2/hooks/featureFlags/pointsEarn/usePointsEarn';
import usePageViewEvent from '@/v2/hooks/usePageViewEvent';
import useViewItemEvent from '@/v2/hooks/useViewItemEvent';
// Legacy components
import DataLayer from '../../../../components/PackagePage/components/DataLayer';
import StickyFooter from '../../ui/StickyFooter';
import AboutHotel from './components/AboutHotel';
import FlyingFrom from './components/FlyingFrom';
import HotelPolicies from './components/HotelPolicies';
import ImageBanner from './components/ImageBanner';
import MobileSearchPanel from './components/MobileSearchPanel';
import PackageHeader from './components/PackageHeader';
import PackageOverview from './components/PackageOverview';
import { RoomOptionsDataProvider } from './contexts/RoomOptionsContext';
import { useSearchPanelControls } from './contexts/SearchPanelContext/SearchPanelContext';
import useCtaOnClick from './hooks/useCtaOnClick';
import useRooms from './hooks/useRooms';

interface Props {
  propertyContent: UI.Property;
  campaign: UI.Campaign | null;
  procuredOffer: UI.ProcuredOffer | null;
  importantInformation: Array<UI.ImportantInformation>;
  navigation: Array<UI.Navigation>;
  warningMessage: UI.Message | null;
  informationMessage: UI.Message | null;
  marketingMessage: UI.MarketingMessage | null;
}

const PackageView = ({
  propertyContent,
  campaign,
  procuredOffer,
  ...rest
}: Props) => {
  const route = useRoute();
  const { calendarRoomId, setCalendarRoomId } = useSearchPanelControls();
  const { searchConfig } = useDestination()!;
  const { isFlightModalOpen } = useFlightModal();
  const { searchQuery, setSearchQuery } = useSearchQuery();
  const { packageOption } = searchQuery || {};
  const {
    roomOptions,
    loading,
    isPreSearch,
    packageOffers,
    flightPromoCodes,
    flightOptions,
  } = useRooms();

  usePageViewEvent({ type: 'package' });

  const shouldShowPointsEarn = usePointsEarn();

  const isPackagePageV3Enabled = usePackagePageV3();

  const {
    originCode,
    setOriginCode,
    isReady: isOriginCodeReady,
  } = useFlyingFrom(searchConfig?.originCodes);

  const {
    campaignRoomOptions,
    isCampaignAvailable,
    showCampaignPackageOptionVariants,
    nonCampaignAltRoomOption,
  } = useFlightCampaignAvailability({
    flightPromoCodes,
    roomOptions,
    campaign,
  });

  const lastSearchCausedSwitchToCampaignOptions = useAutoTurnOnCampaign({
    flightPromoCodes,
    isCampaignAvailable,
  });

  const {
    packageOptionsForSelection,
    availablePackageOptions,
    selectedPackageOption,
  } = usePackageOptions({
    showCampaignPackageOptionVariants,
    isCampaignAvailable,
    campaign,
    procuredOffer,
    originCode,
  });

  const flightInclusion = useFlightInclusion({
    campaign,
    flightPromoCodes,
    isCampaignAvailable,
  });

  const selectedRoomOption = useAutoSelectedRoomOption({
    isLoading: loading,
    isPreSearch,
    roomOptions: campaignRoomOptions?.all,
    calendarRoomId,
    setCalendarRoomId,
  });

  const { handleInvokeViewItemEvent, isReady } = useViewItemEvent({
    property: {
      propertyContent,
      procuredOffer,
    },
    originCode,
  });

  useEffect(() => {
    if (isReady && isOriginCodeReady) {
      handleInvokeViewItemEvent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    originCode,
    selectedPackageOption,
    selectedRoomOption,
    isReady,
    isOriginCodeReady,
  ]);

  const [isPackageOptionsOpen, setIsPackageOptionOpen] = useState(false);
  const [pendingPackageOption, setPendingPackageOption] = useState<string>('');
  const onCtaClick = useCtaOnClick(() => setIsPackageOptionOpen(true));

  const onSelectPackageOption = async (value: string) => {
    setPendingPackageOption(value);
    setSearchQuery({ packageOption: value });
  };

  useEffect(() => {
    if (isPackagePageV3Enabled) setIsPackageOptionOpen(isPackagePageV3Enabled);
  }, [isPackagePageV3Enabled]);

  useEffect(() => {
    if (
      (isPackagePageV3Enabled &&
        pendingPackageOption === selectedPackageOption?.id) ||
      (pendingPackageOption === BEST_RATE_ID && !selectedPackageOption)
    ) {
      setIsPackageOptionOpen(false);
      setPendingPackageOption('');
    }
  }, [isPackagePageV3Enabled, pendingPackageOption, selectedPackageOption]);

  return (
    <Layout {...rest}>
      <RoomOptionsDataProvider
        value={{
          route,
          isPreSearch,
          selectedRoomOption,
          onCtaClick,
          roomOptions: campaignRoomOptions,
          isLoading: loading,
          isOriginSelected: !!searchQuery?.originCode,
          isPackageSelected: !!searchQuery?.packageOption,
          isPackageOptionsOpen,
          setIsPackageOptionOpen,
          isBestRates: searchQuery?.packageOption === BEST_RATE_ID,
          travellerCount: getTotalTravellers(searchQuery),
          packageOffers,
          flightInclusion,
          showCampaignPackageOptionVariants,
          isCampaignAvailable,
          selectedPackageOption,
          nonCampaignAltRoomOption,
          lastSearchCausedSwitchToCampaignOptions,
          packageOptions: availablePackageOptions,
          flightOptions,
        }}
      >
        {isFlightModalOpen ? (
          <Box data-testid="PACKAGE_PAGE_FLIGHT_OPTIONS_VIEW" height="100%">
            <DataLayer pageType="Package Page Flights View" />
            <FlightOptionsView />
          </Box>
        ) : (
          <>
            <DataLayer pageType="Package Page" />
          </>
        )}
        <Flex flexDirection="column" bg="background.card">
          <Section elementKey="photos">
            <Container py={8}>
              <SkeletonProvider isLoading={loading}>
                <PackageHeader />
              </SkeletonProvider>
            </Container>
            <ImageBanner />
          </Section>
          <InPageNavigation navItems={PACKAGE_PAGE_NAV_ITEMS} />

          {shouldShowPointsEarn && (
            <Container>
              <QantasPointBanner
                title="Book & Earn 10,000 Qantas Points on all Jetstar Holidays bookings*"
                description="Simply add your Qantas Frequent Flyer number when completing a booking. Ends 30 April 2025."
              />
            </Container>
          )}

          <Section elementKey="overview">
            <PackageOverview />
            <Divider my="0" width="100%" />
            <ValueProposition py="6" withTitle />
          </Section>
          <Divider my="0" width="100%" />
          <Section elementKey="flyingFrom">
            <FlyingFrom
              value={originCode}
              onSubmit={setOriginCode}
              originCodes={searchConfig?.originCodes}
            />
          </Section>
          <Divider my="0" width="100%" />
          <Section elementKey="package">
            <PackageOptionContainer
              isPanelOpen={isPackageOptionsOpen}
              isPackagePageV3Enabled={isPackagePageV3Enabled}
              setIsPanelOpen={setIsPackageOptionOpen}
              packageOptions={packageOptionsForSelection}
              selectedInclusionSetId={packageOption}
              onSelectPackageOption={onSelectPackageOption}
              disabled={!originCode}
              propertyContent={propertyContent}
              procuredOffer={procuredOffer}
            />
          </Section>
          <Divider my="0" width="100%" />
          <Section elementKey="room">
            <RoomOptions />
          </Section>
          <Divider my="0" width="100%" />
          <Section elementKey="about">
            <AboutHotel />
          </Section>
          <Section elementKey="policies">
            <HotelPolicies />
          </Section>
          <Box pt="10">
            <Container>
              <ImportantInformation />
            </Container>
          </Box>
        </Flex>
        <Box display={['initial', null, 'none']}>
          <StickyFooter>
            <SkeletonProvider isLoading={loading}>
              <MobileSearchPanel packageOptions={availablePackageOptions} />
            </SkeletonProvider>
          </StickyFooter>
        </Box>
      </RoomOptionsDataProvider>
    </Layout>
  );
};

export default PackageView;
